import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const [slug, setSlug] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setSlug(data.slug);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setTimeout(() => navigate(`/vault/${data.slug}`), 1800);
          return;
        }
        if (["failed", "expired"].includes(data.payment_status) || attempts.current > 12) {
          setStatus("failed");
          return;
        }
      } catch {
        if (attempts.current > 12) { setStatus("failed"); return; }
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#050505] grid place-items-center text-center px-6">
      <div className="max-w-md">
        {status === "checking" && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-[#00F0FF] mx-auto" />
            <h1 className="font-display text-2xl font-bold mt-6">Confirming your payment…</h1>
            <p className="text-white/50 mt-2">Hang tight, this only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-14 w-14 text-emerald-400 mx-auto" />
            <h1 className="font-display text-2xl font-bold mt-6">Purchase complete!</h1>
            <p className="text-white/50 mt-2">Your clip is unlocked. Redirecting you back to the vault…</p>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="h-14 w-14 text-red-400 mx-auto" />
            <h1 className="font-display text-2xl font-bold mt-6">Payment not completed</h1>
            <p className="text-white/50 mt-2">Something went wrong or the payment was cancelled.</p>
            <button
              onClick={() => navigate(slug ? `/vault/${slug}` : "/")}
              className="mt-6 bg-[#00F0FF] text-black font-semibold rounded-full px-6 py-3 hover:scale-105 transition-transform duration-300"
            >
              Back to vault
            </button>
          </>
        )}
      </div>
    </div>
  );
}
